import { ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  approveFirmwareByQualityAssurance,
  getDevicesOnProduct,
  getFirmware,
  getFirmwareStatistic,
  getProductionFirmware,
  Result,
  setFirmwareAsCurrent,
  setProductionFirmware,
} from '../../api/api';
import {
  IFirmware,
  IFirmwareStatistic,
  IProductionFirmware,
} from '../../api/types';
import { Section } from '../Common/Section';
import { FallbackSection } from '../Common/Fallback';
import { Step } from './Step';
import { PrepareListOfDevicesForFirmware } from './PrepareListOfDevicesForFirmware';
import { Button } from '../Common/Button';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import { FirmwareInformation } from './FirmwareInformation';
import { PrepareNumberOfDevicesForFirmware } from './PrepareNumberOfDevicesForFirmware';
import {
  FIRMWARE_ROLLOUT_LIMITS,
  getFirmwareWorkflowState,
} from './firmwareWorkflow';
import { canBeRolledOut } from '../../api/rules';
import { useDataRefresh } from '../Hooks/useDataRefresh';

export interface IStep {
  component: ReactNode;
  success: boolean;
}

type Params = {
  productCode: string;
  firmwareVersion: string;
  variant: string;
};

export const Firmware = () => {
  const {
    productCode = '',
    firmwareVersion = '',
    variant = '',
  } = useParams<Params>();
  const [firmware, setFirmware] = useState<Result<IFirmware>>({
    status: '',
  });

  const [firmwareStatistic, setFirmwareStatistic] = useState<
    Result<IFirmwareStatistic>
  >({ status: '' });

  const [devicesOnProduct, setDevicesOnProduct] = useState<
    Result<number>
  >({ status: '' });

  const [productionFirmware, setProductionFirmwareState] = useState<
    Result<IProductionFirmware | null>
  >({ status: '' });

  const [approveFeedback, setApproveFeedback] = useState('');
  const [setCurrentFeedback, setSetCurrentFeedback] = useState('');
  const [setManufacturingFeedback, setSetManufacturingFeedback] =
    useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isSettingCurrent, setIsSettingCurrent] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSettingProduction, setIsSettingProduction] =
    useState(false);
  const [refreshKey, requestRefresh] = useDataRefresh();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  useEffect(() => {
    (async (): Promise<void> => {
      const firmwareResult = await getFirmware(
        productCode,
        firmwareVersion,
        variant,
      );
      setFirmware(firmwareResult);
      const statisticResult = await getFirmwareStatistic(
        productCode,
        firmwareVersion,
        variant,
      );
      setFirmwareStatistic(statisticResult);
      const devicesOnProductResult = await getDevicesOnProduct(
        productCode,
        variant,
      );
      setDevicesOnProduct(devicesOnProductResult);
      const productionFirmwareResult = await getProductionFirmware(
        parseInt(productCode),
        variant,
      );
      setProductionFirmwareState(productionFirmwareResult);
    })();
  }, [productCode, variant, firmwareVersion, refreshKey]);

  const setApprovedFirmwareByQualityAssurance = async () => {
    if (
      firmware.status !== 'OK' ||
      firmwareStatistic.status !== 'OK' ||
      !canBeRolledOut(firmware.data, firmwareStatistic.data) ||
      firmware.data.approvedByQualityAssurance ||
      firmwareStatistic.data.reportedCount <= 0 ||
      isApproving
    ) {
      return;
    }
    setApproveFeedback('');
    setIsApproving(true);
    try {
      const result = await approveFirmwareByQualityAssurance(
        productCode,
        firmwareVersion,
        variant,
      );
      switch (result.status) {
        case 'OK':
          setFirmware(result);
          break;
        case 'Forbidden':
          setApproveFeedback("You don't have permission to do this.");
          break;
        case 'Error':
        case 'Not Found':
        case 'Network issue':
          setApproveFeedback(
            `${result.status}: ${result.errorMessages.join(', ')}`,
          );
          break;
        default:
          setApproveFeedback('Something went wrong...');
          break;
      }
    } finally {
      setIsApproving(false);
    }
  };
  const setAsCurrent = async () => {
    if (
      firmware.status !== 'OK' ||
      firmwareStatistic.status !== 'OK' ||
      !canBeRolledOut(firmware.data, firmwareStatistic.data) ||
      !firmware.data.approvedByQualityAssurance ||
      firmware.data.isCurrent ||
      isSettingCurrent
    ) {
      return;
    }

    setSetCurrentFeedback('');
    setIsSettingCurrent(true);
    try {
      const result = await setFirmwareAsCurrent(
        productCode,
        variant,
        firmwareVersion,
      );
      switch (result.status) {
        case 'OK':
          setFirmware(result);
          break;
        case 'Forbidden':
          setSetCurrentFeedback(
            "You don't have permission to do this.",
          );
          break;
        case 'Error':
        case 'Not Found':
        case 'Network issue':
          setSetCurrentFeedback(
            `${result.status}: ${result.errorMessages.join(', ')}`,
          );
          break;
        default:
          setSetCurrentFeedback('Something went wrong...');
          break;
      }
    } finally {
      setIsSettingCurrent(false);
    }
  };

  const handleSetProductionFirmware = async () => {
    if (
      firmware.status !== 'OK' ||
      firmwareStatistic.status !== 'OK' ||
      !canBeRolledOut(firmware.data, firmwareStatistic.data) ||
      !firmware.data.approvedByQualityAssurance ||
      isCurrentProductionFirmware ||
      isSettingProduction
    ) {
      setShowConfirmation(false);
      return;
    }

    setShowConfirmation(false);
    setSetManufacturingFeedback('');
    setIsSettingProduction(true);
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);

    const result = await setProductionFirmware(
      parseInt(productCode),
      variant,
      parseInt(firmwareVersion),
    );

    clearInterval(timerRef.current);
    timerRef.current = null;
    setIsSettingProduction(false);
    switch (result.status) {
      case 'OK':
        setSetManufacturingFeedback(
          'Production firmware set successfully.',
        );
        setProductionFirmwareState(result);
        break;
      case 'Forbidden':
        setSetManufacturingFeedback(
          "You don't have permission to do this.",
        );
        break;
      case 'Error':
      case 'Not Found':
      case 'Network issue':
        setSetManufacturingFeedback(
          `${result.status}: ${result.errorMessages.join(', ')}`,
        );
        break;
      default:
        setSetManufacturingFeedback('Something went wrong...');
        break;
    }
  };

  const isCurrentProductionFirmware =
    productionFirmware.status === 'OK' &&
    productionFirmware.data?.version === firmwareVersion;

  if (firmware.status !== 'OK') {
    if (
      firmware.status === 'Error' ||
      firmware.status === 'Not Found' ||
      firmware.status === 'Network issue' ||
      firmware.status === 'Forbidden'
    ) {
      return (
        <Section>
          <div>
            <div className="text-xl pb-3">Information</div>
            <div className="font-light">
              <div>
                <span>Firmware ID:</span>
                <span> {firmwareVersion}</span>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xl pb-3">
              Status: {firmware.status}
            </div>
            <div className="font-light">
              <div>
                {firmware.errorMessages.map((x, i) => {
                  return <div key={i}>{x}</div>;
                })}
              </div>
            </div>
          </div>
        </Section>
      );
    }
    return (
      <Section>
        <div>
          <div className="text-xl pb-3">Information</div>
          <div className="font-light">
            <div>
              <span>Firmware ID:</span>
              <span> {firmwareVersion}</span>
            </div>
          </div>
        </div>
        <div>
          <div className="text-xl pb-3">Status</div>
          <div className="font-light">
            <div>Unknown firmware Id</div>
          </div>
        </div>
      </Section>
    );
  }

  if (firmwareStatistic.status !== 'OK') {
    return <FallbackSection result={firmwareStatistic} />;
  }

  if (devicesOnProduct.status !== 'OK') {
    return <FallbackSection result={devicesOnProduct} />;
  }

  const workflow = getFirmwareWorkflowState(
    firmware.data,
    firmwareStatistic.data,
    isCurrentProductionFirmware,
  );
  const numberOfdevicesWithThisFirmware =
    firmwareStatistic.data.reportedCount;

  return (
    <div className="mt-10">
      <FirmwareInformation
        firmware={firmware}
        firmwareStatistic={firmwareStatistic}
        devicesOnProduct={devicesOnProduct.data}
      />
      <Step
        title="Send firmware to multiple devices"
        subtitle="Sends setSpecificFirmware command to all devices in list"
        number={1}
        disabled={workflow.step1.disabled}
        disabledReason={workflow.step1.reason}
        stepStatus={{
          ok: workflow.hasReportedDevice,
          message: `Atleast ${1} device needs to run firmware ${firmwareVersion}. ${numberOfdevicesWithThisFirmware} are running it`,
        }}
      >
        <PrepareListOfDevicesForFirmware
          disabled={workflow.step1.disabled}
          maxDevices={
            firmware.data.approvedByQualityAssurance
              ? undefined
              : FIRMWARE_ROLLOUT_LIMITS.maxDevicesBeforeQualityApproval
          }
          onSubmitted={requestRefresh}
        />
      </Step>
      <div className="p-6" hidden={workflow.rolloutEligible}>
        This firmware can not be rolled out further.
      </div>
      <div hidden={!workflow.rolloutEligible}>
        <Step
          title="Accepted by QA"
          subtitle="QA needs to verify that the firmware works"
          number={2}
          disabled={workflow.step2.disabled}
          disabledReason={workflow.step2.reason}
          stepStatus={{
            ok: firmware.data.approvedByQualityAssurance,
            message: `Firmware ${firmwareVersion} needs to be accepted by QA`,
          }}
        >
          <Button
            disabled={[
              {
                disabled: !workflow.rolloutEligible,
                reason: workflow.step2.reason ?? '',
              },
              {
                disabled: !workflow.hasReportedDevice,
                reason: 'At least one device has to run the firmware',
              },
              {
                disabled: firmware.data.approvedByQualityAssurance,
                reason: 'Firmware already approved',
              },
              {
                disabled: isApproving,
                reason: 'Approval is being submitted.',
              },
            ]}
            onClick={setApprovedFirmwareByQualityAssurance}
            text={
              isApproving
                ? 'Approving...'
                : firmware.data.approvedByQualityAssurance
                  ? `Accepted`
                  : `Accept ${firmwareVersion}`
            }
          />
          {approveFeedback && (
            <span role="alert">{approveFeedback}</span>
          )}
        </Step>
        <Step
          disabled={workflow.step3.disabled}
          disabledReason={workflow.step3.reason}
          stepStatus={{
            ok: true,
            message: 'OK',
          }}
          title={'Update N devices'}
          subtitle={
            <UpdateNDevicesSubtitle
              isApprovedByQA={
                firmware.data.approvedByQualityAssurance
              }
              isCurrent={firmware.data.isCurrent}
            />
          }
          number={3}
        >
          <PrepareNumberOfDevicesForFirmware
            productCode={productCode}
            firmware={firmware.data}
            variant={variant}
            disabled={workflow.step3.disabled}
            disabledReason={workflow.step3.reason}
            maxDevicesPerRequest={workflow.maxDevicesPerRequest}
            onSubmitted={requestRefresh}
          />
        </Step>
        <Step
          title="Set firmware as current"
          subtitle="All devices will now upgrade to this when get upgrade to latest command"
          number={4}
          disabled={workflow.step4.disabled}
          disabledReason={workflow.step4.reason}
          stepStatus={{
            ok: firmware.data.isCurrent,
            message: `Firmware ${firmwareVersion} needs to be set as current.`,
          }}
        >
          <Button
            disabled={[
              {
                disabled: !workflow.rolloutEligible,
                reason: workflow.step4.reason ?? '',
              },
              {
                disabled: !firmware.data.approvedByQualityAssurance,
                reason:
                  'Firmware needs to be approved by quality assurance',
              },
              {
                disabled: firmware.data.isCurrent,
                reason: 'Firmware is already set as current',
              },
              {
                disabled: isSettingCurrent,
                reason:
                  'The current-firmware change is being submitted.',
              },
            ]}
            onClick={setAsCurrent}
            text={
              isSettingCurrent
                ? 'Setting as current...'
                : `Set ${firmwareVersion} as current`
            }
          />
          {setCurrentFeedback && (
            <span role="alert">{setCurrentFeedback}</span>
          )}
        </Step>
        <Step
          disabled={workflow.step5.disabled}
          disabledReason={workflow.step5.reason}
          stepStatus={{
            ok: true,
            message: 'OK',
          }}
          title={'Set firmware as desired for all devices'}
          subtitle={
            <UpdateNDevicesSubtitle
              isApprovedByQA={
                firmware.data.approvedByQualityAssurance
              }
              isCurrent={firmware.data.isCurrent}
            />
          }
          number={5}
        >
          <PrepareNumberOfDevicesForFirmware
            productCode={productCode}
            firmware={firmware.data}
            defaultDevicesToUpgrade={devicesOnProduct.data}
            variant={variant}
            disabled={workflow.step5.disabled}
            disabledReason={workflow.step5.reason}
            maxDevicesPerRequest={workflow.maxDevicesPerRequest}
            onSubmitted={requestRefresh}
          />
        </Step>
      </div>
      <div hidden={!workflow.rolloutEligible}>
        <Step
          title="Set as production firmware"
          subtitle="This firmware will be flashed to new devices in the factory"
          number={6}
          disabled={workflow.step6.disabled}
          disabledReason={workflow.step6.reason}
          stepStatus={{
            ok: isCurrentProductionFirmware,
            message: isCurrentProductionFirmware
              ? `Firmware ${firmwareVersion} is the production firmware`
              : `Firmware ${firmwareVersion} is not the production firmware`,
          }}
        >
          {isSettingProduction ? (
            <div className="text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                <span className="font-medium">
                  Setting production firmware... {elapsedSeconds}s
                </span>
              </div>
              <p className="mt-1 text-gray-500">
                The firmware binary (~5 MB) is being downloaded from
                S3, verified against its MD5 checksum, and saved to
                the database. This typically takes 5–15 seconds.
              </p>
            </div>
          ) : (
            <Button
              disabled={[
                {
                  disabled: !workflow.rolloutEligible,
                  reason: workflow.step6.reason ?? '',
                },
                {
                  disabled: !firmware.data.approvedByQualityAssurance,
                  reason: 'Firmware must be approved by QA',
                },
                {
                  disabled: isCurrentProductionFirmware,
                  reason: 'Already set as production firmware',
                },
                {
                  disabled: isSettingProduction,
                  reason:
                    'Production firmware change is being submitted.',
                },
              ]}
              onClick={() => setShowConfirmation(true)}
              text={`Set ${firmwareVersion} as production firmware`}
            />
          )}
        </Step>
      </div>
      <ConfirmationModal
        isOpen={showConfirmation}
        title="Set Production Firmware"
        message={`Are you sure you want to set firmware version ${firmwareVersion} as the production firmware? This firmware will be flashed to all new devices manufactured in the factory.`}
        confirmText="Yes, set as production"
        cancelText="Cancel"
        onConfirm={handleSetProductionFirmware}
        onCancel={() => setShowConfirmation(false)}
      />
      <ConfirmationModal
        isOpen={setManufacturingFeedback !== ''}
        title="Production Firmware result"
        message={setManufacturingFeedback}
        confirmText="Close"
        cancelText=""
        onConfirm={() => setSetManufacturingFeedback('')}
        onCancel={() => setSetManufacturingFeedback('')}
      />
    </div>
  );
};

interface ISubtitleParams {
  isApprovedByQA: boolean;
  isCurrent: boolean;
}

const UpdateNDevicesSubtitle = ({
  isApprovedByQA,
  isCurrent,
}: ISubtitleParams) => {
  return (
    <ul>
      <li>Max number of devices that can be updated</li>
      <li>
        ✓ default: <b>1000</b>
      </li>
      <li>
        {isApprovedByQA ? '✓' : '✕'} Approved by QA: <b>20 000</b>
      </li>
      <li>
        {isCurrent ? '✓' : '✕'} Set as current: <b>Unlimited</b>
      </li>
    </ul>
  );
};
